import React, { useMemo, useState, useEffect } from "react";
import "./HospitalDashboard.css";
import {
  hospitalListDoctors,
  hospitalGetAllPatients,
  hospitalCreateDoctor,
  hospitalCreatePatient,
  hospitalCreateAppointment,
  hospitalGetAllAppointments,
  hospitalGetAllSessions,
  hospitalUpdatePatientDoctor,
  hospitalUpdateDoctor,
  hospitalDeleteDoctor,
  hospitalUpdatePatient,
  hospitalDeletePatient,
  doctorListSessions,
  doctorListSessionNotes,
} from "../components/api";
import { formatDate, formatDateTime } from "../utils/dateFormatting";
import useTranslation from '../hooks/useTranslation';

import AppShell from "../components/shell/AppShell";
import { LuLayoutDashboard, LuStethoscope, LuUsers, LuPlus } from "react-icons/lu";
import DashboardPage from "../components/hospital/DashboardPage";
import AddDoctorModal from "../components/hospital/modals/AddDoctorModal";
import AddPatientModal from "../components/hospital/modals/AddPatientModal";
import AddAppointmentModal from "../components/hospital/modals/AddAppointmentModal";
import ScheduleAppointmentModal from "../components/hospital/modals/ScheduleAppointmentModal";
import EditDoctorModal from "../components/hospital/modals/EditDoctorModal";
import EditPatientDoctorModal from "../components/hospital/modals/EditPatientDoctorModal";
import EditPatientModal from "../components/hospital/modals/EditPatientModal";
import DeleteDoctorModal from "../components/hospital/modals/DeleteDoctorModal";
import DeletePatientModal from "../components/hospital/modals/DeletePatientModal";
import SessionHistoryModal from "../components/hospital/modals/SessionHistoryModal";
import SessionDetailsModal from "../components/hospital/modals/SessionDetailsModal";
import ReportModal from "../components/hospital/modals/ReportModal";

const getHospitalId = () => {
  try {
    const userId = localStorage.getItem("neuro_user_id");
    if (!userId) return null;
    return userId;
  } catch (e) {
    console.error("Error getting hospital_id:", e);
    return null;
  }
};

export default function HospitalDashboard() {
  const { t } = useTranslation();
  const [activePage, setActivePage] = useState("dashboard");
  const [selectedDoctorId, setSelectedDoctorId] = useState(null);
  const [selectedPatientId, _setSelectedPatientId] = useState(null);
  const [reportFilter, setReportFilter] = useState("Weekly");

  const [doctors, setDoctors] = useState([]);
  const [patients, setPatients] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  const [doctorSearchQuery, setDoctorSearchQuery] = useState("");
  const [patientSearchQuery, setPatientSearchQuery] = useState("");

  const [showAddDoctorModal, setShowAddDoctorModal] = useState(false);
  const [showAddPatientModal, setShowAddPatientModal] = useState(false);
  const [showAddAppointmentModal, setShowAddAppointmentModal] = useState(false);
  const [showScheduleAppointmentModal, setShowScheduleAppointmentModal] = useState(false);
  const [showEditDoctorModal, setShowEditDoctorModal] = useState(false);
  const [showEditPatientModal, setShowEditPatientModal] = useState(false);
  const [showDeleteDoctorModal, setShowDeleteDoctorModal] = useState(false);
  const [showDeletePatientModal, setShowDeletePatientModal] = useState(false);
  const [selectedPatientForSchedule, setSelectedPatientForSchedule] = useState(null);
  const [selectedPatientForEdit, setSelectedPatientForEdit] = useState(null);
  const [selectedDoctorForEdit, setSelectedDoctorForEdit] = useState(null);
  const [selectedDoctorForDelete, setSelectedDoctorForDelete] = useState(null);
  const [selectedPatientForDelete, setSelectedPatientForDelete] = useState(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showSessionHistory, setShowSessionHistory] = useState(false);
  const [selectedPatientForReport, setSelectedPatientForReport] = useState(null);
  const [selectedPatientForHistory, setSelectedPatientForHistory] = useState(null);
  const [sessionHistory, setSessionHistory] = useState([]);
  const [selectedSessionDetails, setSelectedSessionDetails] = useState(null);
  const [sessionNotes, setSessionNotes] = useState([]);
  const [lastSessionNote, setLastSessionNote] = useState('');

  const [doctorForm, setDoctorForm] = useState({ name: "", email: "", phone: "", age: "", gender: "", display_name: "", role_specialization: "" });
  const [patientForm, setPatientForm] = useState({ name: "", age: "", address: "", contact: "", doctor_id: "" });
  const [appointmentForm, setAppointmentForm] = useState({ doctor_id: "", patient_id: "", date: "", time: "", purpose: "", duration_minutes: 30, notes: "" });
  const [scheduleAppointmentForm, setScheduleAppointmentForm] = useState({ doctor_id: "", date: "", time: "", purpose: "", duration_minutes: 30, notes: "" });
  const [editDoctorForm, setEditDoctorForm] = useState({ doctor_id: "" });
  const [editDoctorFullForm, setEditDoctorFullForm] = useState({ name: "", phone: "", age: "", gender: "", display_name: "", role_specialization: "" });
  const [editPatientFullForm, setEditPatientFullForm] = useState({ name: "", age: "", address: "", contact: "", doctor_id: "" });

  const [hospitalId, setHospitalId] = useState(null);

  useEffect(() => {
    const id = getHospitalId();
    setHospitalId(id);
    if (id) {
      loadData(id);
    }
  }, []);

  const loadData = async (hospital_id) => {
    if (!hospital_id) return;
    setLoading(true);
    try {
      const [doctorsRes, patientsRes, appointmentsRes, sessionsRes] = await Promise.all([
        hospitalListDoctors(hospital_id),
        hospitalGetAllPatients(hospital_id),
        hospitalGetAllAppointments(hospital_id),
        hospitalGetAllSessions(hospital_id),
      ]);

      if (doctorsRes.status_code === 200) setDoctors(doctorsRes.data || []);
      if (patientsRes.status_code === 200) setPatients(patientsRes.data || []);
      if (appointmentsRes.status_code === 200) setAppointments(appointmentsRes.data || []);
      if (sessionsRes.status_code === 200) setSessions(sessionsRes.data || []);
    } catch (error) {
      console.error("Error loading data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddDoctor = async (e) => {
    e.preventDefault();
    if (!hospitalId) {
      alert("Hospital ID not found. Please log in again.");
      return;
    }
    try {
      const res = await hospitalCreateDoctor({ ...doctorForm, hospital_id: hospitalId });
      if (res.status_code === 200) {
        alert(res.message || "Doctor created successfully! Invitation email sent.");
        setShowAddDoctorModal(false);
        setDoctorForm({ name: "", email: "", phone: "", age: "", gender: "", display_name: "", role_specialization: "" });
        loadData(hospitalId);
      } else {
        alert(res.message || "Failed to create doctor");
      }
    } catch (error) {
      alert("Error creating doctor: " + (error.message || "Unknown error"));
    }
  };

  const handleAddPatient = async (e) => {
    e.preventDefault();
    if (!hospitalId) {
      alert("Hospital ID not found. Please log in again.");
      return;
    }
    if (!patientForm.doctor_id) {
      alert("Please select a doctor");
      return;
    }
    try {
      const res = await hospitalCreatePatient({ ...patientForm, hospital_id: hospitalId });
      if (res.status_code === 200) {
        alert("Patient created successfully!");
        setShowAddPatientModal(false);
        setPatientForm({ name: "", age: "", address: "", contact: "", doctor_id: "" });
        loadData(hospitalId);
      } else {
        alert(res.message || "Failed to create patient");
      }
    } catch (error) {
      alert("Error creating patient: " + (error.message || "Unknown error"));
    }
  };

  const handleScheduleAppointment = async (e) => {
    e.preventDefault();
    if (!hospitalId || !selectedPatientForSchedule) {
      alert("Missing required information");
      return;
    }
    if (!scheduleAppointmentForm.doctor_id || !scheduleAppointmentForm.date || !scheduleAppointmentForm.time) {
      alert("Please fill in doctor, date, and time");
      return;
    }
    try {
      const res = await hospitalCreateAppointment({
        hospital_id: hospitalId,
        doctor_id: scheduleAppointmentForm.doctor_id,
        patient_id: selectedPatientForSchedule.id,
        date: scheduleAppointmentForm.date,
        time: scheduleAppointmentForm.time,
        purpose: scheduleAppointmentForm.purpose || "",
        duration_minutes: scheduleAppointmentForm.duration_minutes || 30,
        notes: scheduleAppointmentForm.notes || ""
      });
      if (res.status_code === 200) {
        alert("Appointment scheduled successfully! Doctor has been notified.");
        setShowScheduleAppointmentModal(false);
        setScheduleAppointmentForm({ doctor_id: "", date: "", time: "", purpose: "", duration_minutes: 30, notes: "" });
        setSelectedPatientForSchedule(null);
        loadData(hospitalId);
      } else {
        alert(res.message || "Failed to schedule appointment");
      }
    } catch (error) {
      alert("Error scheduling appointment: " + (error.message || "Unknown error"));
    }
  };

  const handleUpdatePatientDoctor = async (e) => {
    e.preventDefault();
    if (!hospitalId || !selectedPatientForEdit) {
      alert("Missing required information");
      return;
    }
    if (!editDoctorForm.doctor_id) {
      alert("Please select a doctor");
      return;
    }

    const futureAppointments = appointments.filter(a =>
      a.patient_id === selectedPatientForEdit.id &&
      a.date && new Date(a.date) > new Date()
    );

    if (futureAppointments.length > 0) {
      const confirmMsg = `This patient has ${futureAppointments.length} future appointment(s). Do you want to proceed with reassignment? (Appointments will remain with the original doctor unless rescheduled)`;
      if (!window.confirm(confirmMsg)) {
        return;
      }
    }

    try {
      const res = await hospitalUpdatePatientDoctor(hospitalId, selectedPatientForEdit.id, editDoctorForm.doctor_id);
      if (res.status_code === 200) {
        alert("Patient doctor updated successfully!");
        setShowEditDoctorModal(false);
        setEditDoctorForm({ doctor_id: "" });
        setSelectedPatientForEdit(null);
        loadData(hospitalId);
      } else {
        alert(res.message || "Failed to update patient doctor");
      }
    } catch (error) {
      alert("Error updating patient doctor: " + (error.message || "Unknown error"));
    }
  };

  const handleUpdateDoctor = async (e) => {
    e.preventDefault();
    if (!hospitalId) {
      alert("Hospital ID not found. Please log in again.");
      return;
    }
    if (!selectedDoctorForEdit) {
      alert("No doctor selected");
      return;
    }
    try {
      const res = await hospitalUpdateDoctor(hospitalId, selectedDoctorForEdit.user_id, editDoctorFullForm);
      if (res.status_code === 200) {
        alert("Doctor updated successfully!");
        setShowEditDoctorModal(false);
        setSelectedDoctorForEdit(null);
        setEditDoctorFullForm({ name: "", phone: "", age: "", gender: "", display_name: "", role_specialization: "" });
        loadData(hospitalId);
      } else {
        alert(res.message || "Failed to update doctor");
      }
    } catch (error) {
      alert("Error updating doctor: " + (error.message || "Unknown error"));
    }
  };

  const handleDeleteDoctor = async () => {
    if (!hospitalId) {
      alert("Hospital ID not found. Please log in again.");
      return;
    }
    if (!selectedDoctorForDelete) {
      alert("No doctor selected");
      return;
    }
    try {
      const res = await hospitalDeleteDoctor(hospitalId, selectedDoctorForDelete.user_id);
      if (res.status_code === 200) {
        alert("Doctor deleted successfully!");
        setShowDeleteDoctorModal(false);
        setSelectedDoctorForDelete(null);
        loadData(hospitalId);
      } else {
        alert(res.message || "Failed to delete doctor");
      }
    } catch (error) {
      alert("Error deleting doctor: " + (error.message || "Unknown error"));
    }
  };

  const handleUpdatePatient = async (e) => {
    e.preventDefault();
    if (!hospitalId) {
      alert("Hospital ID not found. Please log in again.");
      return;
    }
    if (!selectedPatientForEdit) {
      alert("No patient selected");
      return;
    }
    if (!editPatientFullForm.doctor_id) {
      alert("Please select a doctor");
      return;
    }
    try {
      const res = await hospitalUpdatePatient(hospitalId, selectedPatientForEdit.id, editPatientFullForm);
      if (res.status_code === 200) {
        alert("Patient updated successfully!");
        setShowEditPatientModal(false);
        setSelectedPatientForEdit(null);
        setEditPatientFullForm({ name: "", age: "", address: "", contact: "", doctor_id: "" });
        loadData(hospitalId);
      } else {
        alert(res.message || "Failed to update patient");
      }
    } catch (error) {
      alert("Error updating patient: " + (error.message || "Unknown error"));
    }
  };

  const handleDeletePatient = async () => {
    if (!hospitalId) {
      alert("Hospital ID not found. Please log in again.");
      return;
    }
    if (!selectedPatientForDelete) {
      alert("No patient selected");
      return;
    }
    try {
      const res = await hospitalDeletePatient(hospitalId, selectedPatientForDelete.id);
      if (res.status_code === 200) {
        alert("Patient deleted successfully!");
        setShowDeletePatientModal(false);
        setSelectedPatientForDelete(null);
        loadData(hospitalId);
      } else {
        alert(res.message || "Failed to delete patient");
      }
    } catch (error) {
      alert("Error deleting patient: " + (error.message || "Unknown error"));
    }
  };

  const handleAddAppointment = async (e) => {
    e.preventDefault();
    if (!hospitalId) {
      alert("Hospital ID not found. Please log in again.");
      return;
    }
    if (!appointmentForm.doctor_id || !appointmentForm.patient_id) {
      alert("Please select both doctor and patient");
      return;
    }
    try {
      const res = await hospitalCreateAppointment({ ...appointmentForm, hospital_id: hospitalId });
      if (res.status_code === 200) {
        alert("Appointment created successfully! Doctor has been notified.");
        setShowAddAppointmentModal(false);
        setAppointmentForm({ doctor_id: "", patient_id: "", date: "", time: "", purpose: "", duration_minutes: 30, notes: "" });
        loadData(hospitalId);
      } else {
        alert(res.message || "Failed to create appointment");
      }
    } catch (error) {
      alert("Error creating appointment: " + (error.message || "Unknown error"));
    }
  };

  const loadSessionHistory = async (patientId) => {
    try {
      const res = await doctorListSessions(patientId);
      if (res.status_code === 200) {
        setSessionHistory(res.data || []);
        return res.data || [];
      }
      return [];
    } catch (error) {
      console.error('Error loading session history:', error);
      return [];
    }
  };

  const loadSessionNotes = async (sessionId) => {
    try {
      const res = await doctorListSessionNotes(sessionId);
      if (res.status_code === 200) {
        setSessionNotes(res.data || []);
      } else {
        setSessionNotes([]);
      }
    } catch (error) {
      console.error('Error loading session notes:', error);
      setSessionNotes([]);
    }
  };

  const filteredDoctors = useMemo(() => {
    if (!doctorSearchQuery.trim()) {
      return doctors;
    }
    const query = doctorSearchQuery.toLowerCase().trim();
    return doctors.filter((d) => {
      const name = (d.display_name || d.user_name || "").toLowerCase();
      const email = (d.email || d.user_name || "").toLowerCase();
      const role = (d.role_specialization || "").toLowerCase();
      const phone = (d.phone || "").toLowerCase();
      return name.includes(query) || email.includes(query) || role.includes(query) || phone.includes(query);
    });
  }, [doctors, doctorSearchQuery]);

  const selectedDoctor = useMemo(
    () => doctors.find((d) => d.user_id === selectedDoctorId) || null,
    [selectedDoctorId, doctors]
  );
  const selectedPatient = useMemo(
    () => patients.find((p) => p.id === selectedPatientId) || null,
    [selectedPatientId, patients]
  );

  const filteredPatients = useMemo(() => {
    let filtered = selectedDoctor
      ? patients.filter((p) => p.doctor_id === selectedDoctor.user_id)
      : patients;

    if (patientSearchQuery.trim()) {
      const query = patientSearchQuery.toLowerCase().trim();
      filtered = filtered.filter((p) => {
        const name = (p.name || "").toLowerCase();
        const contact = (p.contact || "").toLowerCase();
        const address = (p.address || "").toLowerCase();
        const doctorName = (p.doctor_name || "").toLowerCase();
        const status = (p.status || "").toLowerCase();
        return name.includes(query) || contact.includes(query) || address.includes(query) || doctorName.includes(query) || status.includes(query);
      });
    }

    return filtered;
  }, [patients, selectedDoctor, patientSearchQuery]);

  const PatientDetail = () => {
    const p = selectedPatient;
    if (!p) return <div className="admin-content">Select a patient</div>;

    return (
      <div className="admin-content">
        <div className="admin-panel card-hover" style={{ marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 48, height: 48, borderRadius: '9999px', background: '#1f2937', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700 }}>
              {p.name.split(' ').map(n => n[0]).slice(0, 2).join('')}
            </div>
            <div style={{ flex: 1 }}>
              <div className="admin-card-title" style={{ marginBottom: 2 }}>{p.name} — Age {p.age || "N/A"}</div>
              <div className="admin-muted">Assigned Doctor: {p.doctor_name || "Unassigned"}</div>
            </div>
            <span className={`badge ${p.status === 'High Risk' ? 'badge-danger' : p.status === 'Moderate Risk' ? 'badge-warning' : 'badge-success'}`}>
              {p.status || "Stable"}
            </span>
          </div>
        </div>
        <div className="admin-panel">
          <div className="admin-panel-title">Patient Information</div>
          <div className="admin-grid-2">
            <div>
              <div className="admin-muted">Contact</div>
              <div style={{ marginTop: 6 }}>{p.contact || "N/A"}</div>
            </div>
            <div>
              <div className="admin-muted">Address</div>
              <div style={{ marginTop: 6 }}>{p.address || "N/A"}</div>
            </div>
            <div>
              <div className="admin-muted">Last Session</div>
              <div style={{ marginTop: 6 }}>{p.last_session ? new Date(p.last_session).toLocaleDateString() : "None"}</div>
            </div>
            <div>
              <div className="admin-muted">Next Session</div>
              <div style={{ marginTop: 6 }}>{p.next_session ? new Date(p.next_session).toLocaleDateString() : "Not scheduled"}</div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderPage = () => {
    if (activePage === "dashboard") return <DashboardPage doctors={doctors} patients={patients} sessions={sessions} appointments={appointments} reportFilter={reportFilter} setReportFilter={setReportFilter} />;
    if (activePage === "doctors") {
      return (
        <div className="admin-content">
          <div className="admin-panel">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div className="admin-panel-title">Doctors</div>
              <button className="admin-btn" onClick={() => setShowAddDoctorModal(true)}>+ Add Doctor</button>
            </div>
            <div style={{ marginBottom: 16, position: 'relative' }}>
              <input
                type="text"
                placeholder="Search doctors by name, email, role, or phone..."
                value={doctorSearchQuery}
                onChange={(e) => setDoctorSearchQuery(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  paddingRight: doctorSearchQuery ? '40px' : '10px',
                  fontSize: '14px',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  boxSizing: 'border-box'
                }}
              />
              {doctorSearchQuery && (
                <button
                  onClick={() => setDoctorSearchQuery("")}
                  style={{
                    position: 'absolute',
                    right: '8px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '18px',
                    color: '#6b7280',
                    padding: '4px 8px'
                  }}
                  title="Clear search"
                >
                  ×
                </button>
              )}
            </div>
            {loading ? (
              <div className="admin-muted">{t('Loading...')}</div>
            ) : (
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>{t('Name')}</th>
                    <th>{t('Email')}</th>
                    <th>{t('Role/Specialization')}</th>
                    <th>{t('Patients')}</th>
                    <th>{t('Status')}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDoctors.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="admin-muted">
                        {doctors.length === 0
                          ? t("No doctors yet. Add your first doctor.")
                          : t('No doctors found matching') + ` "${doctorSearchQuery}"`}
                      </td>
                    </tr>
                  ) : (
                    filteredDoctors.map((d) => (
                      <tr key={d.user_id}>
                        <td>{d.display_name || d.user_name}</td>
                        <td>{d.email || d.user_name}</td>
                        <td>{d.role_specialization || "-"}</td>
                        <td>{d.patient_count || 0}</td>
                        <td className="status-active">{t('Active')}</td>
                        <td>
                          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                            <button
                              className="admin-btn"
                              onClick={() => {
                                setSelectedDoctorId(d.user_id);
                                setActivePage("patients");
                              }}
                            >
                              {t('View Patients')}
                            </button>
                            <button
                              className="admin-btn"
                              onClick={() => {
                                setSelectedDoctorForEdit(d);
                                setEditDoctorFullForm({
                                  name: d.display_name || d.user_name || "",
                                  phone: d.phone || "",
                                  age: d.age || "",
                                  gender: d.gender || "",
                                  display_name: d.display_name || "",
                                  role_specialization: d.role_specialization || ""
                                });
                                setShowEditDoctorModal(true);
                              }}
                              style={{ fontSize: '12px', padding: '6px 12px', background: '#10b981', color: '#fff' }}
                            >
                              {t('Edit')}
                            </button>
                            <button
                              className="admin-btn"
                              onClick={() => {
                                setSelectedDoctorForDelete(d);
                                setShowDeleteDoctorModal(true);
                              }}
                              style={{ fontSize: '12px', padding: '6px 12px', background: '#ef4444', color: '#fff' }}
                            >
                              {t('Delete')}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            )}
          </div>
        </div>
      );
    }
    if (activePage === "patients") {
      return (
        <div className="admin-content">
          <div className="admin-actions">
            <button className="admin-btn" onClick={() => setShowAddPatientModal(true)}>+ Add Patient</button>
            {selectedDoctor && (
              <button className="admin-btn" onClick={() => setSelectedDoctorId(null)} style={{ marginLeft: 8 }}>
                Show All Patients
              </button>
            )}
          </div>
          <div className="admin-panel">
            <div className="admin-panel-title">
              {selectedDoctor ? `Patients - ${selectedDoctor.display_name || selectedDoctor.user_name}` : "All Patients"}
            </div>
            <div style={{ marginBottom: 16, position: 'relative' }}>
              <input
                type="text"
                placeholder="Search patients by name, contact, address, doctor, or status..."
                value={patientSearchQuery}
                onChange={(e) => setPatientSearchQuery(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  paddingRight: patientSearchQuery ? '40px' : '10px',
                  fontSize: '14px',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  boxSizing: 'border-box'
                }}
              />
              {patientSearchQuery && (
                <button
                  onClick={() => setPatientSearchQuery("")}
                  style={{
                    position: 'absolute',
                    right: '8px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '18px',
                    color: '#6b7280',
                    padding: '4px 8px'
                  }}
                  title="Clear search"
                >
                  ×
                </button>
              )}
            </div>
            {loading ? (
              <div className="admin-muted">{t('Loading...')}</div>
            ) : (
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>{t('Name')}</th>
                    <th>{t('Doctor')}</th>
                    <th>{t('Age')}</th>
                    <th>{t('Contact')}</th>
                    <th>{t('Status')}</th>
                    <th>{t('Next Session')}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPatients.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="admin-muted">
                        {patients.length === 0
                          ? t("No patients found")
                          : selectedDoctor
                            ? t('No patients found for this doctor') + (patientSearchQuery ? ` ${t('matching')} "${patientSearchQuery}"` : "")
                            : t('No patients found') + (patientSearchQuery ? ` ${t('matching')} "${patientSearchQuery}"` : "")}
                      </td>
                    </tr>
                  ) : (
                    filteredPatients.map((p) => (
                      <tr key={p.id}>
                        <td>{p.name}</td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span>{p.doctor_name || t("Unassigned")}</span>
                            <button
                              onClick={() => {
                                setSelectedPatientForEdit(p);
                                setEditDoctorForm({ doctor_id: p.doctor_id || "" });
                                setShowEditDoctorModal(true);
                              }}
                              style={{
                                background: 'transparent',
                                border: 'none',
                                cursor: 'pointer',
                                padding: '4px',
                                fontSize: '14px',
                                color: '#6b7280'
                              }}
                              title="Edit Doctor"
                            >
                              ✏️
                            </button>
                          </div>
                        </td>
                        <td>{p.age || "-"}</td>
                        <td>{p.contact || "-"}</td>
                        <td>
                          <span className={`badge ${p.status === 'High Risk' ? 'badge-danger' : p.status === 'Moderate Risk' ? 'badge-warning' : p.status === 'No Data' ? 'badge-neutral' : 'badge-success'}`}>
                            {p.status ? t(p.status) : t("No Data")}
                          </span>
                        </td>
                        <td>{formatDateTime(p.next_session, p.next_session_time) || <span className="badge badge-neutral">{t('Not Scheduled')}</span>}</td>
                        <td>
                          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                            <button
                              className="admin-btn"
                              onClick={() => {
                                setSelectedPatientForSchedule(p);
                                setScheduleAppointmentForm({
                                  doctor_id: p.doctor_id || "",
                                  date: "",
                                  time: "",
                                  purpose: "",
                                  duration_minutes: 30,
                                  notes: ""
                                });
                                setShowScheduleAppointmentModal(true);
                              }}
                              style={{ fontSize: '12px', padding: '6px 12px' }}
                            >
                              {t('Schedule Appointment')}
                            </button>
                            <button
                              className="admin-btn"
                              onClick={async () => {
                                setSelectedPatientForReport(p);
                                const history = await loadSessionHistory(p.id);
                                if (history && history.length > 0) {
                                  try {
                                    const notesRes = await doctorListSessionNotes(history[0].session_id);
                                    if (notesRes && notesRes.status_code === 200 && Array.isArray(notesRes.data) && notesRes.data.length > 0) {
                                      setLastSessionNote(notesRes.data[0].note_text || '');
                                    } else {
                                      setLastSessionNote('');
                                    }
                                  } catch (error) {
                                    console.error('Error loading session notes:', error);
                                    setLastSessionNote('');
                                  }
                                } else {
                                  setLastSessionNote('');
                                }
                                setShowReportModal(true);
                              }}
                              style={{ fontSize: '12px', padding: '6px 12px' }}
                            >
                              {t('Report')}
                            </button>
                            <button
                              className="admin-btn"
                              onClick={async () => {
                                setSelectedPatientForHistory(p);
                                await loadSessionHistory(p.id);
                                setShowSessionHistory(true);
                              }}
                              style={{ fontSize: '12px', padding: '6px 12px' }}
                            >
                              {t('History')}
                            </button>
                            <button
                              className="admin-btn"
                              onClick={() => {
                                setSelectedPatientForEdit(p);
                                setEditPatientFullForm({
                                  name: p.name || "",
                                  age: p.age || "",
                                  address: p.address || "",
                                  contact: p.contact || "",
                                  doctor_id: p.doctor_id || ""
                                });
                                setShowEditPatientModal(true);
                              }}
                              style={{ fontSize: '12px', padding: '6px 12px', background: '#10b981', color: '#fff' }}
                            >
                              {t('Edit')}
                            </button>
                            <button
                              className="admin-btn"
                              onClick={() => {
                                setSelectedPatientForDelete(p);
                                setShowDeletePatientModal(true);
                              }}
                              style={{ fontSize: '12px', padding: '6px 12px', background: '#ef4444', color: '#fff' }}
                            >
                              {t('Delete')}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            )}
          </div>
        </div>
      );
    }
    if (activePage === "patient-detail") return <PatientDetail />;
    return null;
  };

  useEffect(() => {
    const originalTheme = document.body.getAttribute("data-theme");
    document.body.setAttribute("data-theme", "light");
    return () => {
      if (originalTheme) {
        document.body.setAttribute("data-theme", originalTheme);
      }
    };
  }, []);

  return (
    <AppShell
      role="hospital"
      className="admin-layout-tabs"
      workspace={t('Hospital administration')}
      title={t('Hospital Dashboard')}
      subtitle={t('Doctors, patients and operational activity')}
      nav={[
        { key: 'dashboard', label: t('Overview'), shortLabel: t('Overview'), icon: <LuLayoutDashboard /> },
        { key: 'doctors', label: t('Doctors'), shortLabel: t('Doctors'), icon: <LuStethoscope /> },
        { key: 'patients', label: t('Patients'), shortLabel: t('Patients'), icon: <LuUsers /> },
      ]}
      activeKey={activePage}
      onNavigate={setActivePage}
      actions={
        <>
          <button type="button" className="xi-btn xi-btn--sm" onClick={() => setShowAddPatientModal(true)}>
            <LuPlus aria-hidden="true" />
            <span className="xi-hide-sm">{t('Patient')}</span>
          </button>
          <button type="button" className="xi-btn xi-btn--primary xi-btn--sm" onClick={() => setShowAddDoctorModal(true)}>
            <LuPlus aria-hidden="true" />
            <span className="xi-hide-sm">{t('Doctor')}</span>
          </button>
        </>
      }
    >
      <div className="admin-content">{renderPage()}</div>

      <AddDoctorModal
        show={showAddDoctorModal}
        onClose={() => setShowAddDoctorModal(false)}
        doctorForm={doctorForm}
        setDoctorForm={setDoctorForm}
        onSubmit={handleAddDoctor}
      />

      <AddPatientModal
        show={showAddPatientModal}
        onClose={() => setShowAddPatientModal(false)}
        patientForm={patientForm}
        setPatientForm={setPatientForm}
        onSubmit={handleAddPatient}
        doctors={doctors}
      />

      <AddAppointmentModal
        show={showAddAppointmentModal}
        onClose={() => setShowAddAppointmentModal(false)}
        appointmentForm={appointmentForm}
        setAppointmentForm={setAppointmentForm}
        onSubmit={handleAddAppointment}
        doctors={doctors}
        patients={patients}
      />

      <ScheduleAppointmentModal
        show={showScheduleAppointmentModal}
        onClose={() => {
          setShowScheduleAppointmentModal(false);
          setSelectedPatientForSchedule(null);
        }}
        selectedPatientForSchedule={selectedPatientForSchedule}
        scheduleAppointmentForm={scheduleAppointmentForm}
        setScheduleAppointmentForm={setScheduleAppointmentForm}
        onSubmit={handleScheduleAppointment}
        doctors={doctors}
      />

      <EditPatientDoctorModal
        show={showEditDoctorModal && selectedPatientForEdit && !selectedDoctorForEdit}
        onClose={() => {
          setShowEditDoctorModal(false);
          setSelectedPatientForEdit(null);
        }}
        selectedPatientForEdit={selectedPatientForEdit}
        editDoctorForm={editDoctorForm}
        setEditDoctorForm={setEditDoctorForm}
        onSubmit={handleUpdatePatientDoctor}
        doctors={doctors}
      />

      <EditDoctorModal
        show={showEditDoctorModal && !!selectedDoctorForEdit}
        onClose={() => {
          setShowEditDoctorModal(false);
          setSelectedDoctorForEdit(null);
        }}
        selectedDoctorForEdit={selectedDoctorForEdit}
        editDoctorFullForm={editDoctorFullForm}
        setEditDoctorFullForm={setEditDoctorFullForm}
        onSubmit={handleUpdateDoctor}
      />

      <DeleteDoctorModal
        show={showDeleteDoctorModal}
        onClose={() => {
          setShowDeleteDoctorModal(false);
          setSelectedDoctorForDelete(null);
        }}
        selectedDoctorForDelete={selectedDoctorForDelete}
        onConfirm={handleDeleteDoctor}
      />

      <EditPatientModal
        show={showEditPatientModal}
        onClose={() => {
          setShowEditPatientModal(false);
          setSelectedPatientForEdit(null);
        }}
        selectedPatientForEdit={selectedPatientForEdit}
        editPatientFullForm={editPatientFullForm}
        setEditPatientFullForm={setEditPatientFullForm}
        onSubmit={handleUpdatePatient}
        doctors={doctors}
      />

      <DeletePatientModal
        show={showDeletePatientModal}
        onClose={() => {
          setShowDeletePatientModal(false);
          setSelectedPatientForDelete(null);
        }}
        selectedPatientForDelete={selectedPatientForDelete}
        onConfirm={handleDeletePatient}
      />

      <SessionHistoryModal
        show={showSessionHistory}
        onClose={() => {
          setShowSessionHistory(false);
          setSelectedPatientForHistory(null);
          setSelectedSessionDetails(null);
        }}
        selectedPatientForHistory={selectedPatientForHistory}
        sessionHistory={sessionHistory}
        loadSessionNotes={loadSessionNotes}
        setSelectedSessionDetails={setSelectedSessionDetails}
      />

      <SessionDetailsModal
        show={!!selectedSessionDetails}
        onClose={() => {
          setSelectedSessionDetails(null);
          setSessionNotes([]);
        }}
        selectedSessionDetails={selectedSessionDetails}
        sessionNotes={sessionNotes}
        setSessionNotes={setSessionNotes}
      />

      <ReportModal
        show={showReportModal}
        onClose={() => setShowReportModal(false)}
        selectedPatientForReport={selectedPatientForReport}
        sessionHistory={sessionHistory}
        lastSessionNote={lastSessionNote}
        onShowHistory={() => {
          setShowReportModal(false);
          setSelectedPatientForHistory(selectedPatientForReport);
          setShowSessionHistory(true);
        }}
      />
    </AppShell>
  );
}
